import { useCallback, useEffect, useRef, useState } from "react";
import {
  HIGHLIGHT_MS,
  LONG_PRESS_MS,
  SWIPE_REPLY_THRESHOLD,
  getReplySenderName,
} from "./chatReplyUtils";

/**
 * Shared reply-to-message state + gestures for all chat screens.
 *
 * @param {object} options
 * @param {Array} options.messages - currently loaded messages (for jump lookup)
 * @param {'msgByUserId'|'sender'} [options.senderField='sender']
 * @param {(quoted: object) => void} [options.onJumpUnavailable] - when original isn't loaded and no context API
 * @param {(quoted: object) => void} [options.onFetchContext] - emit *-message-context when original isn't loaded
 */
const useChatReply = ({
  messages = [],
  senderField = "sender",
  onJumpUnavailable,
  onFetchContext,
} = {}) => {
  const [replyToTarget, setReplyToTarget] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  const messageRefs = useRef({});
  const swipeStartRef = useRef({ x: 0, y: 0, id: null, swiped: false });
  const longPressTimerRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const pendingJumpIdRef = useRef(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const highlightMessage = useCallback((messageId) => {
    if (!messageId) return;
    setHighlightedMessageId(messageId);
    if (highlightTimerRef.current) {
      window.clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedMessageId(null);
      highlightTimerRef.current = null;
    }, HIGHLIGHT_MS);
  }, []);

  const scrollToMessage = useCallback(
    (messageId) => {
      if (!messageId) return false;
      const el =
        messageRefs.current[messageId] ||
        messageRefs.current[String(messageId)];
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      highlightMessage(messageId);
      return true;
    },
    [highlightMessage]
  );

  const handleStartReply = useCallback((msg) => {
    if (!msg?._id) return;
    if (msg.messageType && msg.messageType !== "chat") return;
    setReplyToTarget(msg);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyToTarget(null);
  }, []);

  const clearReplyAfterSend = useCallback(() => {
    setReplyToTarget(null);
  }, []);

  const handleJumpToQuotedMessage = useCallback(
    (quoted) => {
      if (!quoted?._id || quoted.isDeleted) return;

      const quotedId = String(quoted._id);
      const alreadyLoaded = messages.some(
        (m) => m?._id != null && String(m._id) === quotedId
      );
      if (alreadyLoaded) {
        scrollToMessage(quoted._id);
        return;
      }

      if (typeof onFetchContext === "function") {
        pendingJumpIdRef.current = quoted._id;
        setContextLoading(true);
        onFetchContext(quoted);
        return;
      }

      if (typeof onJumpUnavailable === "function") {
        onJumpUnavailable(quoted);
      }
    },
    [messages, onFetchContext, onJumpUnavailable, scrollToMessage]
  );

  /** Call after *-message-context response merges messages into state. */
  const finishContextJump = useCallback(
    (found, messageId) => {
      setContextLoading(false);
      const targetId = messageId || pendingJumpIdRef.current;
      pendingJumpIdRef.current = null;
      if (!found || !targetId) return;
      window.requestAnimationFrame(() => {
        window.setTimeout(() => scrollToMessage(targetId), 50);
      });
    },
    [scrollToMessage]
  );

  const handlePointerDownReply = useCallback(
    (e, msg) => {
      if (msg?.messageType && msg.messageType !== "chat") return;
      const touch = e.touches?.[0];
      if (!touch) return;
      swipeStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        id: msg?._id,
        swiped: false,
      };
      clearLongPressTimer();
      longPressTimerRef.current = window.setTimeout(() => {
        handleStartReply(msg);
      }, LONG_PRESS_MS);
    },
    [clearLongPressTimer, handleStartReply]
  );

  const handlePointerMoveReply = useCallback(
    (e, msg) => {
      const start = swipeStartRef.current;
      if (!start?.id || start.id !== msg?._id) return;
      const touch = e.touches?.[0];
      if (!touch) return;
      const dx = touch.clientX - start.x;
      const dy = Math.abs(touch.clientY - start.y);

      if (dx > 12 || dy > 12) clearLongPressTimer();

      if (dx > SWIPE_REPLY_THRESHOLD && dy < 40 && !start.swiped) {
        swipeStartRef.current = { ...start, swiped: true };
        handleStartReply(msg);
      }
    },
    [clearLongPressTimer, handleStartReply]
  );

  const handlePointerUpReply = useCallback(() => {
    clearLongPressTimer();
    swipeStartRef.current = { x: 0, y: 0, id: null, swiped: false };
  }, [clearLongPressTimer]);

  const getBubbleGestureHandlers = useCallback(
    (msg) => ({
      onTouchStart: (e) => handlePointerDownReply(e, msg),
      onTouchMove: (e) => handlePointerMoveReply(e, msg),
      onTouchEnd: handlePointerUpReply,
      onTouchCancel: handlePointerUpReply,
      onContextMenu: (e) => {
        e.preventDefault();
        handleStartReply(msg);
      },
    }),
    [
      handlePointerDownReply,
      handlePointerMoveReply,
      handlePointerUpReply,
      handleStartReply,
    ]
  );

  const setMessageRef = useCallback((messageId, el) => {
    if (messageId) messageRefs.current[messageId] = el;
  }, []);

  const replySenderName = replyToTarget
    ? getReplySenderName(replyToTarget, senderField) ||
    getReplySenderName(
      { msgByUserId: replyToTarget.msgByUserId, sender: replyToTarget.sender },
      senderField
    )
    : "";

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }
    };
  }, [clearLongPressTimer]);

  return {
    replyToTarget,
    replySenderName,
    senderField,
    highlightedMessageId,
    contextLoading,
    setContextLoading,
    handleStartReply,
    handleCancelReply,
    clearReplyAfterSend,
    handleJumpToQuotedMessage,
    finishContextJump,
    getBubbleGestureHandlers,
    setMessageRef,
    scrollToMessage,
  };
};

export default useChatReply;
